/**
 * The lofi finish for the front's tracks (lane FRONT-SOUND). `lofify` takes a track written in jianpu and turns
 * it into a lofi wuxia piece without touching its tune: slower tempo, looser hands, softer dynamics, a swung
 * "boom-bap" of big drum, small drum, wood and cymbals (a dotted eighth + sixteenth shuffle, ghost notes), and a
 * warm zheng comp (a rolled root-and-fifth arpeggio on each bar's drone root) under the pad. The vinyl hiss and
 * crackle are made by the front's audio (front/audio.ts), not here. Nothing sampled; the lines are original.
 * (No track is registered by this file.)
 */
import { arp, octave } from '../compose';
import type { PartDef, SectionDef, TrackDef } from '../types';

/** Four-beat bars of a beat, swung. D big drum, d small drum, r wood, c small cymbals, G gong; `p` is a ghost. */
const BEATS = {
  /** Boom-bap: kick, snare on 2 and 4, shuffled off-beat hats. */
  boom: [`D_. 0__ d_ c_p 0_ D_ d_ c_p`, `D_. 0__ d_ c_p D_ 0_ d_ c_p`, `D_. 0__ d_ c_p 0_ D_ d_ c_p`],
  /** Dark half-time: slow kick and one snare, a gong on the turn. */
  dusk: [`D_. 0__ 0 d 0_ D_`, `D_. 0__ 0 d D_ 0_`, `D_. 0__ 0 d 0_ D_`],
  /** Beatless-ish: a soft kick, a brush, a bell tick. */
  soft: [`D 0_ c_p 0 0_ c_p`, `D 0 c_p 0_ r_p 0_`, `D 0_ c_p 0 0_ c_p`],
  /** Rain: woodblock rim and drizzle hats. */
  rain: [`D_. 0__ r_p c_p 0_ D_ r_ c_p`, `D_. 0__ c_p c_p 0_ D_ r_ c_p`, `D_. 0__ r_p c_p 0_ D_ r_ c_p`],
} as const;
export type LofiBeat = keyof typeof BEATS;

/** The last bar of a four: a small turn-round. */
const FILL: Record<LofiBeat, string> = {
  boom: `D_. 0__ d_ c_p d_ d_p D_ d_`,
  dusk: `G- 0 d`,
  soft: `D 0 c_p c_p 0_ b_`,
  rain: `D_. 0__ r_ r_p c_p c_p D_ r_`,
};

const SOFTER: Record<string, string> = { ff: 'mp', f: 'mp', mf: 'mp' };

/** The root of each bar of a drone line ("6,,&3,--- | 5,,&2,---" → "6,, 5,,"). */
function rootsOf(pad: string): string {
  return pad
    .split('|')
    .map((b) => b.trim().split(/\s+/)[0]!.split('&')[0]!.replace(/[^1-7#b',]/g, ''))
    .filter(Boolean)
    .join(' ');
}

export function lofify(def: TrackDef, o: { bpm: number; beat: LofiBeat; comp?: boolean }): TrackDef {
  const pat = BEATS[o.beat];
  const sections: Record<string, SectionDef> = {};
  for (const [name, s] of Object.entries(def.sections)) {
    const parts: Record<string, string> = {};
    for (const [p, line] of Object.entries(s.parts)) {
      const perc = def.parts[p]?.inst === 'perc';
      if (p === 'drums') continue;
      parts[p] = perc ? line : line.replace(/@(\w+)/g, (m, d: string) => (SOFTER[d] ? `@${SOFTER[d]}` : m));
    }
    const bars = Array.from({ length: s.bars }, (_, i) =>
      i % 4 === 3 ? FILL[o.beat] : pat[Math.floor(i / 4 + i) % pat.length]!,
    );
    parts.beat = `@p ${bars.join(' | ')}`;
    const pad = s.parts.pad;
    if (o.comp !== false && pad) {
      const roots = octave(rootsOf(pad), 1);
      parts.comp = `@pp ${arp(roots, [0, null, 2, null, 4, null, 2, null])}`;
    }
    sections[name] = { ...s, parts, ...(s.bpm ? { bpm: Math.round(s.bpm * (o.bpm / def.bpm)) } : {}) };
  }
  const parts: Record<string, PartDef> = {};
  for (const [p, d] of Object.entries(def.parts)) if (p !== 'drums') parts[p] = d;
  parts.beat = { inst: 'perc', gain: 0.3, pan: 0, reverb: 0.25 };
  if (o.comp !== false) parts.comp = { inst: 'zheng', gain: 0.4, pan: 0.2, reverb: 0.5 };
  return { ...def, bpm: o.bpm, humanize: 0.6, sections, parts };
}
